import { useMemo } from "react";
import { useRouter } from "next/router";
import { UserType, User } from "@ecologi/shared";
import { FeedbackForm } from "@ecologi/react-components/src/FeedbackForm";
import { Achievements } from "@ecologi/react-components/src/Achievements";
import { forkResult, parseQueryObject } from "../../../lib";
import { useStore } from "../../../store";
import { Head } from "../../../components";
import { prefetchProfileForSSR, useQueryProfile } from "../profile-page";
import { getFormattedUserType } from "../profile-page/utils";
import { createGeneralFeedback } from "../../../lib/api/general-feedback";
import { ProfileSSRParams } from "../profile-page/api";

function ProfileAchievementsData({
  profile: baseProfile,
}: {
  profile: UserType | User;
}) {
  const router = useRouter();
  const { badgeShareId } = parseQueryObject(router.query);
  const { user } = useStore();

  const isOwnProfile =
    !!baseProfile && !!user && user.userId === baseProfile.userId;

  const profileAsUserType = useMemo(
    () => getFormattedUserType(baseProfile),
    [baseProfile]
  );

  const shareDescription = `Check out all of the Ecologi achievements ${
    isOwnProfile ? "you've" : `${profileAsUserType.fullName()} has`
  } unlocked`;

  const shareImageUrl = `https://offset-earth-share-images.s3-eu-west-1.amazonaws.com/achievements/share_${
    badgeShareId ? `${badgeShareId}_16x9` : "default"
  }.png`;

  return (
    <>
      <Head
        description={shareDescription}
        imageHeight="675"
        imageUrl={shareImageUrl}
        imageWidth="1200"
        title={`${
          isOwnProfile ? "Your" : profileAsUserType.fullNamePlural
        } Badges`}
      />
      <Achievements isOwnProfile={isOwnProfile} user={profileAsUserType} />
      {isOwnProfile && (
        <FeedbackForm
          onSubmit={createGeneralFeedback}
          currentUser={profileAsUserType}
          currentPath={router.asPath}
        />
      )}
    </>
  );
}

export function ProfileAchievementsPage({
  profile,
}: {
  profile: UserType | undefined;
}) {
  const router = useRouter();
  const { usernameOrId, childUsernameOrId } = router.query;

  const profileResult =
    !profile && useQueryProfile(usernameOrId, childUsernameOrId);

  return profile ? (
    <ProfileAchievementsData profile={profile} />
  ) : (
    forkResult<UserType | User>(
      {
        loading: Achievements.Loading,
        data: (profile) => <ProfileAchievementsData profile={profile} />,
      },
      profileResult
    )
  );
}

ProfileAchievementsPage.prefetchData = async (
  params: ProfileSSRParams,
  accessToken?: string
) => {
  const profile = await prefetchProfileForSSR(params, accessToken);

  return {
    profile: profile || null,
    layoutProps: {
      isLightHeader: false,
      isProfile: true,
    },
  };
};
